import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { AlertCircle, Clock, TrendingUp } from 'lucide-react';

const reasons = [
  {
    code: '01',
    icon: AlertCircle,
    title: 'Bridging the Gap',
    description: 'Millions of talented individuals around the world lack access to the tools and knowledge needed to compete in the digital economy.',
  },
  {
    code: '02',
    icon: Clock,
    title: 'Modernizing Systems',
    description: "Businesses are losing time, money, and opportunities due to outdated systems that can't keep pace with innovation.",
  },
  {
    code: '03',
    icon: TrendingUp,
    title: 'Future-Ready Solutions',
    description: 'The future of finance, work, and innovation is evolving — and no one should be left behind.',
  },
];

export function WhySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="why"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="why-section"
    >
      <div ref={ref} className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 lg:sticky lg:top-32 self-start"
          >
            <p className="eyebrow section-eyebrow-line mb-6">
              Why We Exist
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-stark leading-tight tracking-tight mb-6">
              Closing the{' '}
              <span className="text-gradient">Digital Divide</span>
            </h2>
            <p className="text-lg text-stark-2 leading-relaxed">
              ServeNetics exists to close that gap. We believe technology should
              be an equalizer, not a divider — creating pathways for everyone to
              participate in the digital future.
            </p>
          </motion.div>

          {/* Right - Reasons */}
          <div className="lg:col-span-7">
            <div className="space-y-px bg-line-1 border border-line-1 rounded-md overflow-hidden">
              {reasons.map((reason, index) => (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, x: 30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  className="flex gap-6 p-6 md:p-8 bg-surface hover:bg-surface-2 transition-colors duration-300"
                  data-testid={`reason-card-${index}`}
                >
                  <div className="flex flex-col items-start gap-3 flex-shrink-0">
                    <span className="eyebrow text-signal">REASON.{reason.code}</span>
                    <div className="w-11 h-11 rounded-md bg-ink-2 border border-line-1 flex items-center justify-center">
                      <reason.icon className="w-5 h-5 text-stark" />
                    </div>
                  </div>
                  <div className="pt-1">
                    <h3 className="font-heading text-xl font-semibold text-stark mb-2 tracking-tight">
                      {reason.title}
                    </h3>
                    <p className="text-stark-2 leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default WhySection;
